/**
 * Converts a date and time into an integer for sorting.
 * This function handles the conversion of a 12-hour clock format to a 24-hour format.
 * It then combines the date and time into a single sortable integer.
 *
 * @param {String} date - The date in 'Y-m-d' format (e.g., '2023-12-09').
 * @param {String} time - The time in 'h:i A' format (e.g., '1:00 PM').
 * @return {Number|undefined} A sortable integer representing the combined date and time.
 */
const convertDateToSortableInteger = ( date, time ) => {
	// Check if date or time is empty or null
	if ( ! date || ! time ) {
		return;
	}

	// Convert the date string to a number by removing dashes
	// For example, '2023-12-09' becomes 20231209
	const dateAsNumber = parseInt( date.replaceAll( '-', '' ) );

	// Extract hours and minutes from the time string
	// If time format is invalid, this will return null
	const timeMatches = time.match( /\d+/g );

	// If time format is invalid (no matches), return undefined
	if ( ! timeMatches ) {
		return;
	}

	// Parse the extracted time parts to numbers
	let [ hours, minutes ] = timeMatches.map( Number );

	// Convert 12-hour format to 24-hour format
	// Add 12 to the hours if PM is found and it's not 12 PM
	// Set hours to 0 if it's 12 AM
	if ( time.includes( 'PM' ) && hours !== 12 ) {
		hours += 12;
	} else if ( time.includes( 'AM' ) && hours === 12 ) {
		hours = 0;
	}

	// Combine hours and minutes into a time number
	// For example, 1:00 PM becomes 1300
	const timeAsNumber = hours * 100 + minutes;

	// Check if the resulting numbers are valid
	if ( isNaN( dateAsNumber ) || isNaN( timeAsNumber ) ) {
		return;
	}

	// Return the combined date and time as a sortable integer
	// For example, '2023-12-09' at '1:00 PM' becomes 202312091300
	return dateAsNumber * 10000 + timeAsNumber;
};

export default convertDateToSortableInteger;
